/**
 * USP bar marquee.
 *
 * The bar is one row that shares its leftover space equally between the items.
 * Once the labels no longer fit — a narrow viewport, longer copy, a bigger font,
 * one more item — the row would clip, so this element clones the items and
 * scrolls the row instead. While everything fits, nothing moves.
 *
 * The clone is what makes the loop seamless: the animation travels exactly one
 * set plus one gap, at which point the copy sits where the original started.
 */

/** Constant speed, so a longer row takes proportionally longer rather than racing. */
const PIXELS_PER_SECOND = 45;

class UspMarquee extends HTMLElement {
  /** @type {HTMLElement | null} */
  #list = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    this.#list = this.querySelector('.usp-bar__list');
    if (!this.#list) return;

    this.#update();

    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#resizeObserver.observe(this);

    // The fallback face is narrower than Jost, so the first measurement can say
    // the row fits when the real one will not.
    document.fonts?.ready.then(() => this.#schedule());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #schedule() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const list = this.#list;
    if (!list) return;

    // Always measure the bare row — clones from an earlier pass would double it.
    for (const clone of list.querySelectorAll('[data-clone]')) clone.remove();
    this.removeAttribute('marquee');

    // Items hidden on mobile have no boxes and must not stretch the measurement.
    const items = Array.from(list.children).filter((item) => item.getClientRects().length > 0);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;

    const rowWidth = last.getBoundingClientRect().right - first.getBoundingClientRect().left;

    // A pixel of slack: sub-pixel layout should not set the whole band moving.
    if (rowWidth <= list.clientWidth + 1) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const distance = rowWidth + gap;

    for (const item of items) {
      const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-clone', '');
      list.append(clone);
    }

    this.style.setProperty('--usp-marquee-distance', `${distance}px`);
    this.style.setProperty('--usp-marquee-duration', `${distance / PIXELS_PER_SECOND}s`);
    this.setAttribute('marquee', '');
  }
}

if (!customElements.get('usp-marquee')) {
  customElements.define('usp-marquee', UspMarquee);
}
